import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Button, Card, Field } from '../components/ui';
import { authApi } from '../api/client';

/**
 * PUBLIC /forgot-password — request a password-reset email.
 *
 * Enumeration-safe: the API always returns {ok:true}, so we show the same
 * confirmation message whether or not the email exists. We treat any thrown
 * error the same way (still show the neutral confirmation) to avoid leaking
 * account existence via differing UX.
 */
export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      await authApi.requestPasswordReset(email);
    } catch {
      /* enumeration-safe: ignore errors, show the same confirmation */
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-box">
        <h1 className="auth-title">App Builder</h1>
        <Card title="Reset your password">
          {sent ? (
            <>
              <Banner tone="success">
                If that email exists, a reset link was sent.
              </Banner>
              <p className="auth-alt">
                <Link to="/login">Back to sign in</Link>
              </p>
            </>
          ) : (
            <>
              <form onSubmit={onSubmit} className="form">
                <Field
                  label="Email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Button type="submit" loading={busy}>
                  Send reset link
                </Button>
              </form>
              <p className="auth-alt">
                Remembered it? <Link to="/login">Sign in</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
